import {
  type ReactElement,
  type ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useBlocker, useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * One "Speichern" for a whole tab, modelled on the settings of the customer portal.
 *
 * Every form of the tab that would have had a plain "Speichern" registers here instead and
 * keeps its own endpoint, its own audit row and its own answer. The bar saves the changed ones
 * one after another, in page order. A form with a named action ("Live schalten", "Neue Fassung
 * veröffentlichen", uploads) keeps its own button and never joins.
 */

/**
 * `invalid`: the browser showed what is missing, the run stops there. `cancelled`: the person
 * stopped the section's save in a question it asked; the run stops without an error.
 */
export type SaveResult = "ok" | "invalid" | "cancelled" | "failed";

export interface SaveSection {
  /** Names the section in the bar's report. */
  readonly label: string;
  /** Changed by the user: saving is offered and leaving asks first. */
  readonly dirty: boolean;
  /** Something to save that nobody typed, such as a proposal: saving is offered, leaving is free. */
  readonly pending?: boolean;
  /** Said quietly beside "Speichern" while nothing waits, e.g. what a save does here. */
  readonly hint?: string;
  readonly save: () => Promise<SaveResult>;
}

interface Entry extends SaveSection {
  readonly id: string;
}

export interface RunReport {
  readonly saved: readonly string[];
  /** Refused by the server or not sent; each section shows its reason itself. */
  readonly failed: readonly string[];
  /** The section whose input stopped the run. */
  readonly invalid: string | null;
  /** The section whose save the person cancelled, which stopped the run. */
  readonly cancelled: string | null;
}

const wanted = (entry: SaveSection): boolean => entry.dirty || entry.pending === true;

/**
 * Saves the wanted sections one after another. The list is read again before every step: a
 * save can reload the page and take a section away, and its old closure must not run.
 * Invalid input stops the run, because the browser has put the focus on it; a refusal does
 * not, because the sections are independent writes.
 */
export async function runDirty(entries: () => readonly Entry[]): Promise<RunReport> {
  const saved: string[] = [];
  const failed: string[] = [];
  for (const id of entries()
    .filter(wanted)
    .map((entry) => entry.id)) {
    const entry = entries().find((candidate) => candidate.id === id);
    if (entry === undefined || !wanted(entry)) {
      continue;
    }
    let result: SaveResult;
    try {
      result = await entry.save();
    } catch (error: unknown) {
      console.error("[SaveBar]", entry.label, error);
      result = "failed";
    }
    if (result === "invalid") {
      return { saved, failed, invalid: entry.label, cancelled: null };
    }
    if (result === "cancelled") {
      return { saved, failed, invalid: null, cancelled: entry.label };
    }
    (result === "ok" ? saved : failed).push(entry.label);
  }
  return { saved, failed, invalid: null, cancelled: null };
}

/** The bar's sentence for a finished run. */
export function reportText(report: RunReport): { readonly error: boolean; readonly text: string } {
  const done = report.saved.length > 0 ? `Gespeichert: ${report.saved.join(", ")}. ` : "";
  if (report.invalid !== null) {
    return {
      error: true,
      text: `${done}Bitte »${report.invalid}« vervollständigen und erneut speichern.`,
    };
  }
  if (report.cancelled !== null) {
    const refused =
      report.failed.length > 0 ? `Nicht gespeichert: ${report.failed.join(", ")}. ` : "";
    return {
      error: report.failed.length > 0,
      text: `${done}${refused}Speichern bei »${report.cancelled}« abgebrochen.`,
    };
  }
  if (report.failed.length > 0) {
    return {
      error: true,
      text: `Nicht gespeichert: ${report.failed.join(", ")}. Die Gründe stehen bei den Abschnitten.`,
    };
  }
  return { error: false, text: "Gespeichert." };
}

interface Registry {
  readonly add: (id: string, section: RefObject<SaveSection | null>) => () => void;
  readonly mark: (id: string, dirty: boolean, pending: boolean, hint: string | undefined) => void;
  /** Runs the bar as its button does: whether everything wanted is saved now. */
  readonly saveAll: () => Promise<boolean>;
  /** Where the bar stands instead of under the page, `null` for its own place. */
  readonly moveTo: (slot: HTMLElement | null) => void;
}

const SaveBarContext = createContext<Registry | null>(null);

const NOTHING_TO_SAVE = (): Promise<boolean> => Promise.resolve(true);

/**
 * For a named step that works on what is stored (»Einladen« sends the saved address): the bar
 * saves the changes first, and the step goes ahead only when that answered `true`. Without a
 * bar there is nothing to save first.
 */
export function useSaveFirst(): () => Promise<boolean> {
  return useContext(SaveBarContext)?.saveAll ?? NOTHING_TO_SAVE;
}

/**
 * Sent, bubbling, by a section the server refused, so a page that hides parts of itself (the
 * design tab's views and elements, D49) can bring it back in sight: the bar names the section,
 * the reason stands there.
 */
export const REFUSED_EVENT = "countdown:refused";

export function announceRefusal(node: Element | null): void {
  node?.dispatchEvent(new CustomEvent(REFUSED_EVENT, { bubbles: true }));
}

/**
 * Whether anything under the bar is unsaved, for a note that depends on it ("Stil übernehmen"
 * copies only what is saved, D49). A context of its own: in the registry's it would change the
 * registry on every keystroke and sign every section up again.
 */
const DirtyContext = createContext(false);

export function useSaveBarDirty(): boolean {
  return useContext(DirtyContext);
}

/** The set with or without `id`, the same object when nothing changes. */
function toggled(set: ReadonlySet<string>, id: string, on: boolean): ReadonlySet<string> {
  if (set.has(id) === on) {
    return set;
  }
  const next = new Set(set);
  if (on) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
}

/** The map with `id` saying `hint`, or without `id`; the same object when nothing changes. */
function withHint(
  map: ReadonlyMap<string, string>,
  id: string,
  hint: string | undefined,
): ReadonlyMap<string, string> {
  if (map.get(id) === hint) {
    return map;
  }
  const next = new Map(map);
  if (hint === undefined) {
    next.delete(id);
  } else {
    next.set(id, hint);
  }
  return next;
}

/**
 * `reportValidity` for a form the bar saves: the bar sits over the foot of the page, so the
 * first invalid field is scrolled to the middle rather than just into view, where it may hide
 * behind the bar.
 */
export function reportValidityAboveBar(form: HTMLFormElement): boolean {
  if (form.checkValidity()) {
    return true;
  }
  // A field folded away in `Advanced` is opened first: the browser cannot point at a field it
  // does not show, and the person would see the bar stop without a reason.
  const invalid = form.querySelector("input:invalid, select:invalid, textarea:invalid");
  for (let node = invalid?.parentElement ?? null; node !== null; node = node.parentElement) {
    if (node instanceof HTMLDetailsElement) {
      node.open = true;
    }
  }
  form.reportValidity();
  invalid?.scrollIntoView({ block: "center" });
  return false;
}

/**
 * Joins the page's bar while mounted; `null` stays out. Answers whether a bar took the
 * section, so the caller hides its own button only then: without a bar (a test, a dialog's
 * own form) every form keeps its button.
 */
export function useSaveSection(section: SaveSection | null): boolean {
  const registry = useContext(SaveBarContext);
  const id = useId();
  const latest = useRef<SaveSection | null>(section);
  useLayoutEffect(() => {
    latest.current = section;
  });
  const active = registry !== null && section !== null;
  const dirty = section?.dirty ?? false;
  const pending = section?.pending ?? false;
  const hint = section?.hint;

  useEffect(() => (active ? registry.add(id, latest) : undefined), [active, registry, id]);
  useEffect(() => {
    if (active) {
      registry.mark(id, dirty, pending, hint);
    }
  }, [active, registry, id, dirty, pending, hint]);
  return active;
}

/**
 * The bar's place inside a modal dialog (D94): a modal makes the page behind it inert, so the
 * bar under the page could not be reached while the door's dialog is open. Mounted, it takes the
 * one bar, same sections and same report; unmounted, the bar goes back under the page.
 */
export function SaveBarSlot(): ReactElement {
  const registry = useContext(SaveBarContext);
  const place = useCallback(
    (slot: HTMLDivElement | null) => {
      registry?.moveTo(slot);
    },
    [registry],
  );
  return <div ref={place} />;
}

/**
 * Around every page of an application (D43, 22.10.2026): the bar under the page and the guard
 * against leaving it. One bar for the backoffice and one for the platform administration.
 */
export function SaveBarProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const sections = useRef(new Map<string, RefObject<SaveSection | null>>());
  const saveAll = useRef<() => Promise<boolean>>(NOTHING_TO_SAVE);
  const [ids, setIds] = useState<readonly string[]>([]);
  const [dirtyIds, setDirty] = useState<ReadonlySet<string>>(() => new Set());
  const [pendingIds, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const [hints, setHints] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<ReturnType<typeof reportText> | null>(null);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const { pathname } = useLocation();

  const registry = useMemo<Registry>(
    () => ({
      add: (id, section) => {
        sections.current.set(id, section);
        setIds((current) => [...current, id]);
        return () => {
          sections.current.delete(id);
          setIds((current) => current.filter((entry) => entry !== id));
          setDirty((current) => toggled(current, id, false));
          setPending((current) => toggled(current, id, false));
          setHints((current) => withHint(current, id, undefined));
        };
      },
      mark: (id, dirty, pending, hint) => {
        setDirty((current) => toggled(current, id, dirty));
        setPending((current) => toggled(current, id, pending));
        setHints((current) => withHint(current, id, hint));
      },
      saveAll: () => saveAll.current(),
      moveTo: setSlot,
    }),
    [],
  );

  const dirty = dirtyIds.size > 0;
  const savable = dirty || pendingIds.size > 0;

  // A report belongs to the tab it was made on, and to the changes it answered.
  useEffect(() => {
    setReport(null);
  }, [pathname]);
  useEffect(() => {
    if (savable) {
      setReport((current) => (current?.error === false ? null : current));
    }
  }, [savable]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
    };
  }, [dirty]);

  const run = async (): Promise<boolean> => {
    if (!savable) {
      return true;
    }
    if (busy) {
      return false;
    }
    setBusy(true);
    setReport(null);
    try {
      const result = await runDirty(() =>
        [...sections.current].flatMap(([id, section]) =>
          section.current === null ? [] : [{ ...section.current, id }],
        ),
      );
      setReport(reportText(result));
      return result.failed.length === 0 && result.invalid === null && result.cancelled === null;
    } finally {
      setBusy(false);
    }
  };
  useLayoutEffect(() => {
    saveAll.current = run;
  });

  // A proposal nobody touched is no change of the user's: it says what waits, and leaving
  // the tab does not ask about it.
  const proposals = [...pendingIds]
    .map((id) => sections.current.get(id)?.current?.label)
    .filter((label) => label !== undefined)
    .map((label) => `»${label}«`)
    .join(", ");
  const message =
    report?.text ??
    (dirty
      ? "Ungespeicherte Änderungen"
      : savable
        ? `Vorschlag noch nicht gespeichert: ${proposals}`
        : "");
  // Only while nothing waits: then the bar has nothing else to say.
  const hint = report === null && !savable ? [...hints.values()].join(" ") : "";
  const moved = (bar: ReactElement): ReactNode => (slot === null ? bar : createPortal(bar, slot));

  return (
    <SaveBarContext.Provider value={registry}>
      <div>
        <DirtyContext.Provider value={dirty}>{children}</DirtyContext.Provider>
        {ids.length > 0 ? (
          moved(
            <div
              role="region"
              aria-label="Speichern"
              className="sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-end gap-3 border border-border bg-card px-4 py-3 shadow-[var(--countdown-shadow-elevated)]"
            >
              <p
                role="status"
                className={`mr-auto text-sm ${report?.error === true ? "text-destructive" : "text-muted-foreground"}`}
              >
                {message}
              </p>
              {hint === "" ? null : <p className="text-xs text-muted-foreground">{hint}</p>}
              <Button
                type="button"
                aria-disabled={!savable || busy}
                className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                onClick={() => {
                  void run();
                }}
              >
                {busy ? "Wird gespeichert …" : "Speichern"}
              </Button>
            </div>,
          )
        ) : null}
      </div>
      <AlertDialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open && blocker.state === "blocked") {
            blocker.reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ungespeicherte Änderungen</AlertDialogTitle>
            <AlertDialogDescription>
              Sie haben Änderungen vorgenommen, die noch nicht gespeichert wurden. Möchten Sie die
              Seite wirklich verlassen? Ihre Änderungen gehen dann verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Auf der Seite bleiben</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                // Closing would reset the blocker before it proceeds; the state closes it.
                event.preventDefault();
                if (blocker.state === "blocked") {
                  blocker.proceed();
                }
              }}
            >
              Verlassen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SaveBarContext.Provider>
  );
}
